import { ApprovalTaskStatues, Roles, APPROVAL_CATEGORY_CODES } from "common";
import { useOrgIdWithRel } from "hooks/useOrgIdWithRel";
import { useRoles } from "hooks/useRoles";
import { maxBy } from "lodash";
import { approvalTaskStatuesOption } from "pages/ApprovalDashboard/ApprovalDashboard.data";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetApprovalTaskQuery } from "redux/services/approvalProcessor";
import { ApprovalTasksResponse } from "redux/services/approvalProcessor/types";
import { useGetProfileQuery } from "redux/services/membership";
import { getOrganisationMembership, safeParseJSON } from "utils";

interface ApprovalDetailsServiceResult {
  approvalTasks?: ApprovalTasksResponse;
  isGetApprovalTasksLoading: boolean;
  isGetApprovalTasksError: boolean;
  isEnableForChangeStatus: boolean;
  isMissingPrefund: boolean;
  isMissingDisbursementDate: boolean;
  isMissingDisbursementReport: boolean;
  missingPrefundAndDisbursementDateMessage: string;
  getApprovalTasks: () => void;
  getApprovalStatus: () =>
    | {
        value: ApprovalTaskStatues;
        label: string;
        data: {
          colorScheme: string;
        };
      }
    | undefined;
}

// Helper function to get status label and color scheme by status type
const getStatusLabelAndColor = (statusType: ApprovalTaskStatues) => {
  const option = approvalTaskStatuesOption.find((opt) => opt.value === statusType);
  return {
    label: option?.label,
    colorScheme: option?.data?.colorScheme,
  };
};

// Helper function to determine status for BusinessApprover based on approval category
const getBusinessApproverStatus = (approvalCategoryCode?: string) => {
  const isPSPFlow = approvalCategoryCode === APPROVAL_CATEGORY_CODES.PAYROLL_FILE_SYSTEM_PSP;
  const statusType = isPSPFlow
    ? ApprovalTaskStatues.CHECKER_APPROVED
    : ApprovalTaskStatues.APPROVER_APPROVED;
  return getStatusLabelAndColor(statusType);
};

export const useApprovalDetailsService = (
  selectedDisbursementDate?: Date | undefined
): ApprovalDetailsServiceResult => {
  const {
    isPSPMakerCheckerRole,
    isPaymentOpsRole,
    isInHouseCheckerRole,
    isPSPCheckerRole,
    isInhouseApproverRole,
    isPSPApproverRole,
  } = useRoles();
  const { paymentId } = useParams() as any;
  const { orgIdWithRel } = useOrgIdWithRel();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const orgToken = membershipOrganisation?.token;

  const {
    data: approvalTasks,
    isError,
    isFetching,
    refetch,
  } = useGetApprovalTaskQuery(
    {
      referenceNumber: paymentId,
      orgToken,
      orgId: isPSPMakerCheckerRole ? orgIdWithRel : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const getApprovalStatus = useCallback(() => {
    const approvalTask = approvalTasks?.data[0];

    const status = approvalTaskStatuesOption.find(
      (option) => option.value === approvalTask?.status
    );

    if (status?.value === ApprovalTaskStatues.PROCESSING) {
      const defaultStatus = getStatusLabelAndColor(ApprovalTaskStatues.CHECKER_REJECTED);
      let statusLabel = defaultStatus.label;
      let statusColorScheme = defaultStatus.colorScheme;

      const listApprover = approvalTasks?.data[0].approvalTasks?.filter(
        (approver) => approver.status === ApprovalTaskStatues.APPROVED
      );
      const approver = maxBy(listApprover, (approver) => approver?.levelOrder);

      if (approver?.levelId === Roles.BusinessAdmin) {
        const adminStatus = getStatusLabelAndColor(ApprovalTaskStatues.APPROVER_APPROVED);
        statusLabel = adminStatus.label;
        statusColorScheme = adminStatus.colorScheme;
      }

      if (approver?.levelId === Roles.BusinessApprover) {
        const approvalCategoryCode = approvalTasks?.data[0]?.approvalCategoryCode;
        const bizApproverStatus = getBusinessApproverStatus(approvalCategoryCode);
        statusLabel = bizApproverStatus.label;
        statusColorScheme = bizApproverStatus.colorScheme;
      }

      if (approver?.levelId === Roles.BUApprover) {
        const buStatus = getStatusLabelAndColor(ApprovalTaskStatues.APPROVER_APPROVED);
        statusLabel = buStatus.label;
        statusColorScheme = buStatus.colorScheme;
      }

      status.label = statusLabel ?? status.label;
      status.data.colorScheme = statusColorScheme ?? status.data.colorScheme;
    }

    if (status?.value === ApprovalTaskStatues.REJECTED) {
      const defaultStatus = getStatusLabelAndColor(ApprovalTaskStatues.CHECKER_REJECTED);
      let statusLabel = defaultStatus.label;
      let statusColorScheme = defaultStatus.colorScheme;

      const listRejecter = approvalTasks?.data[0].approvalTasks.filter(
        (approver) => approver.status === ApprovalTaskStatues.REJECTED
      );
      const rejecter = maxBy(listRejecter, (approver) => approver?.levelOrder);

      if (rejecter?.levelId === Roles.BusinessAdmin) {
        const adminStatus = getStatusLabelAndColor(ApprovalTaskStatues.APPROVER_DISAPPROVED);
        statusLabel = adminStatus.label;
        statusColorScheme = adminStatus.colorScheme;
      }

      if (rejecter?.levelId === Roles.PaymentOps) {
        const opsStatus = getStatusLabelAndColor(ApprovalTaskStatues.PAYMENT_OPS_DECLINED);
        statusLabel = opsStatus.label;
        statusColorScheme = opsStatus.colorScheme;
      }

      if (rejecter?.levelId === Roles.BUApprover) {
        const buStatus = getStatusLabelAndColor(ApprovalTaskStatues.APPROVER_DISAPPROVED);
        statusLabel = buStatus.label;
        statusColorScheme = buStatus.colorScheme;
      }

      status.label = statusLabel ?? status.label;
      status.data.colorScheme = statusColorScheme ?? status.data.colorScheme;
    }

    return status;
  }, [approvalTasks]);

  const isBusinessApproverRole = isInHouseCheckerRole || isPSPCheckerRole;

  const isEnableForChangeStatus =
    (isPaymentOpsRole &&
      getApprovalStatus()?.value === ApprovalTaskStatues.PROCESSING) ||
    (isBusinessApproverRole &&
      getApprovalStatus()?.value === ApprovalTaskStatues.PENDING) ||
    ((isInhouseApproverRole || isPSPApproverRole) &&
      getApprovalStatus()?.label === "Reviewed");

  const approvalTask = approvalTasks?.data[0];

  const overallInformation = useMemo(
    () => safeParseJSON(approvalTask?.processVariables?.overallInformation),
    [approvalTask?.processVariables?.overallInformation]
  );

  const approverRejected = approvalTask?.approvalTasks?.find(
    (appover) => appover?.status === ApprovalTaskStatues.REJECTED
  );

  const disbursementDate = overallInformation?.disbursementDate;

  const hasApprovalTasks = approvalTasks?.data && approvalTasks.data.length > 0;
  const isNotLoadingOrRejected =
    !isFetching && !approverRejected && hasApprovalTasks;

  const isMissingPrefund =
    isNotLoadingOrRejected &&
    (isInhouseApproverRole || isPSPApproverRole) &&
    !overallInformation?.relatedDocumentId;

  const isMissingDisbursementDate =
    isNotLoadingOrRejected &&
    (isInhouseApproverRole || isPSPApproverRole) &&
    !disbursementDate &&
    !selectedDisbursementDate;

  const isMissingDisbursementReport =
    isNotLoadingOrRejected &&
    isPaymentOpsRole &&
    !overallInformation?.outputDocumentId;

  const missingPrefundAndDisbursementDateMessage: string = useMemo(() => {
    if (isMissingPrefund && isMissingDisbursementDate)
      return "Please upload the Proof of Prefund and select Disbursement Date to continue";

    if (isMissingPrefund)
      return "Please upload the Proof of Prefund to continue";

    if (isMissingDisbursementDate)
      return "Please select Disbursement Date to continue";

    return "";
  }, [isMissingPrefund, isMissingDisbursementDate]);

  return {
    approvalTasks,
    isGetApprovalTasksLoading: isFetching,
    isGetApprovalTasksError: isError,
    isEnableForChangeStatus,
    isMissingPrefund: isMissingPrefund ?? false,
    isMissingDisbursementDate: isMissingDisbursementDate ?? false,
    isMissingDisbursementReport: isMissingDisbursementReport ?? false,
    missingPrefundAndDisbursementDateMessage,
    getApprovalStatus,
    getApprovalTasks: () => {
      refetch();
    },
  };
};
